/**
 * SENTRY-INTEGRATION-01 — Frontend Sentry adapter.
 *
 * Initializes @sentry/react with:
 *   - browserTracingIntegration (performance, sampleRate 0.1)
 *   - replayIntegration (sessionSampleRate 0, errorSampleRate 1.0)
 *   - release = import.meta.env.VITE_APP_VERSION || "dev"
 *   - environment = auto-detected (development | preview | production)
 *
 * Safe to call multiple times: init is idempotent.
 * No-op when VITE_SENTRY_DSN is not configured.
 */
import * as Sentry from "@sentry/react";
import { getSentryClientConfig } from "@/lib/sentry/sentryConfig.functions";

export interface SentryStatus {
  configured: boolean;
  dsnMasked: string | null;
  environment: string;
  release: string;
}

let initialized = false;
let resolvedDsn: string | null = null;
let resolvedEnvironment = "production";
let resolvedRelease = "dev";

function getDsn(): string | undefined {
  if (resolvedDsn) return resolvedDsn;
  try {
    const fromEnv = (import.meta as unknown as { env?: Record<string, string> })
      .env?.VITE_SENTRY_DSN;
    return fromEnv || undefined;
  } catch {
    return undefined;
  }
}

function detectEnvironment(): "development" | "preview" | "production" {
  try {
    const mode = (import.meta as unknown as { env?: Record<string, string> }).env
      ?.MODE;
    if (mode === "development") return "development";
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      if (host.includes("-preview-") || host.includes("lovableproject.com")) {
        return "preview";
      }
    }
    return "production";
  } catch {
    return "production";
  }
}

function getRelease(): string {
  if (resolvedRelease && resolvedRelease !== "dev") return resolvedRelease;
  try {
    const env = (import.meta as unknown as { env?: Record<string, string> }).env;
    return env?.VITE_APP_VERSION || env?.VITE_DEPLOY_ID || "dev";
  } catch {
    return "dev";
  }
}

export async function initSentry(): Promise<void> {
  if (initialized) return;
  // Fetch config from server (DSN is stored as runtime secret SENTRY_DSN).
  try {
    const cfg = await getSentryClientConfig();
    if (cfg.dsn) resolvedDsn = cfg.dsn;
    if (cfg.environment) resolvedEnvironment = cfg.environment;
    if (cfg.release) resolvedRelease = cfg.release;
  } catch (e) {
    console.warn("[sentry] failed to fetch client config", e);
  }
  const dsn = getDsn();
  if (!dsn) return;
  try {
    Sentry.init({
      dsn,
      environment: resolvedDsn ? resolvedEnvironment : detectEnvironment(),
      release: getRelease(),
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],
      tracesSampleRate: 0.1,
      // Session Replay: only on errors (no continuous recording)
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      // Filter noisy/expected errors
      ignoreErrors: [
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",
        "Non-Error promise rejection captured",
      ],
    });
    initialized = true;
  } catch (e) {
    console.warn("[sentry] init failed", e);
  }
}

export function getSentryStatus(): SentryStatus {
  const dsn = getDsn();
  return {
    configured: !!dsn && initialized,
    dsnMasked: dsn ? `${dsn.slice(0, 14)}…` : null,
    environment: resolvedDsn ? resolvedEnvironment : detectEnvironment(),
    release: getRelease(),
  };
}

export function captureException(
  err: unknown,
  context?: Record<string, unknown>,
): void {
  if (!getDsn()) {
    console.error("[sentry:noop]", err, context);
    return;
  }
  Sentry.captureException(err, context ? { extra: context } : undefined);
}

export function captureMessage(
  msg: string,
  context?: Record<string, unknown>,
): void {
  if (!getDsn()) {
    console.warn("[sentry:noop]", msg, context);
    return;
  }
  Sentry.captureMessage(msg, context ? { extra: context } : undefined);
}

export { Sentry };
